import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1615260478820-ddc3a5051ff2";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success("Welcome back, brother."); navigate(res.user?.role === "supporter" ? "/supporter" : "/dashboard"); return; }
    if (res.status === 403) { setNeedsVerify(true); toast.error(res.error); return; }
    toast.error(res.error);
  };

  const resend = async () => {
    setResending(true);
    const res = await resendVerification(email);
    setResending(false);
    if (res.ok) toast.success("Verification email sent. Check your inbox.");
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-bone">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/40" />
        <div className="absolute bottom-16 left-12 text-bone max-w-md">
          <p className="font-serif text-5xl italic leading-tight">"Come to me, all who are weary."</p>
          <span className="font-mono text-xs uppercase tracking-widest text-bone/70 mt-4 block">Matthew 11:28</span>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-md">
          <Link to="/" className="block"><img src="/logo.png" alt="In His Image Movement Ministries" className="h-24 w-auto object-contain mx-auto" /></Link>
          <h1 className="font-serif font-light text-5xl mt-10 tracking-tight text-center">Welcome back.</h1>
          <p className="font-sans text-charcoal/60 mt-3 text-center">Sign in to continue your journey.</p>

          <button onClick={googleLogin} data-testid="google-login-btn"
            className="w-full mt-10 border border-charcoal/20 py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-sand transition-colors flex items-center justify-center gap-3">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-charcoal/15" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">or</span>
            <div className="flex-1 h-px bg-charcoal/15" />
          </div>

          <form onSubmit={submit} className="space-y-6">
            <Input label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
            <Input label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
            <div className="flex justify-end -mt-2">
              <Link to="/forgot-password" data-testid="forgot-password-link"
                className="font-mono text-[11px] uppercase tracking-widest text-charcoal/50 hover:text-olive transition-colors">
                Forgot password?
              </Link>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="group w-full bg-charcoal text-bone py-4 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
              {loading ? "Signing in..." : "Sign In"} <ArrowRight size={16} className="hover-arrow" />
            </button>
          </form>
          {needsVerify && (
            <div className="mt-6 border border-gold/40 bg-gold/10 p-4 text-center" data-testid="login-verify-notice">
              <p className="font-sans text-sm text-charcoal/70">Your email isn't verified yet. Didn't get the link?</p>
              <button onClick={resend} disabled={resending} data-testid="login-resend-btn"
                className="mt-2 font-mono text-xs uppercase tracking-widest text-olive border-b border-olive/40 hover:text-charcoal transition-colors disabled:opacity-50">
                {resending ? "Sending..." : "Resend verification email"}
              </button>
            </div>
          )}
          <p className="font-sans text-sm text-charcoal/60 mt-8">
            New here? <Link to="/register" className="text-olive border-b border-olive/40">Join the movement</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required data-testid={testid}
        className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-olive transition-colors" />
    </div>
  );
}
